import React from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import CapitolBackdrop from "./CapitolBackdrop";
import MiniDashboard from "./MiniDashboard";
import { BrowserFrame, scrollToId } from "./MarketingUI";

export default function Hero() {
  const reduce = useReducedMotion();
  const rise = (delay) => reduce
    ? {}
    : { initial: { opacity: 0, y: 26 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] } };

  return (
    <section id="top" className="relative overflow-hidden bg-brand-navy-900 pt-32 sm:pt-36" data-testid="hero-section">
      {/* depth wash — restrained, solid-leaning */}
      <div className="absolute inset-0" style={{ background: "radial-gradient(1200px 500px at 50% -10%, rgba(63,127,181,0.28), transparent 60%), linear-gradient(180deg, #0B2842 0%, #0E3252 100%)" }} />
      <CapitolBackdrop />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <motion.p {...rise(0)} className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-civic-100" data-testid="hero-eyebrow">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" /> Legislative Workflow &amp; Intelligence
          </motion.p>

          <motion.h1 {...rise(0.08)} className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="hero-headline">
            Legislation moves fast.
            <span className="block bg-gradient-to-r from-brand-gold to-[#F0C558] bg-clip-text text-transparent">Your organization should move faster.</span>
          </motion.h1>

          <motion.p {...rise(0.16)} className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/70 sm:text-lg" data-testid="hero-sub">
            GovFlowState brings legislative tracking, internal workflow, analysis, assignments, alerts, and reporting into one secure platform built for government teams.
          </motion.p>

          <motion.div {...rise(0.24)} className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row" data-testid="hero-ctas">
            <button onClick={() => scrollToId("contact")} className="group inline-flex items-center gap-2 rounded-full bg-brand-gold px-7 py-3.5 text-sm font-bold text-brand-navy-900 shadow-[0_10px_30px_-8px_rgba(214,160,29,0.7)] transition-transform hover:-translate-y-0.5" data-testid="hero-request-demo">
              Request a Demo <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button onClick={() => scrollToId("platform")} className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur transition-colors hover:bg-white/10" data-testid="hero-explore">
              Explore the Platform
            </button>
          </motion.div>

          <motion.p {...rise(0.32)} className="mt-6 text-xs font-medium tracking-wide text-white/45" data-testid="hero-trustline">
            Built for the way government teams actually work.
          </motion.p>
        </div>

        {/* Product reveal overlapping the hero bottom */}
        <motion.div
          initial={reduce ? false : { opacity: 0, y: 60, rotateX: 8 }}
          animate={reduce ? {} : { opacity: 1, y: 0, rotateX: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative z-10 mx-auto mt-14 max-w-5xl [perspective:1600px] sm:mt-20"
          data-testid="hero-product-reveal"
        >
          <BrowserFrame url="app.govflowstate.com/dashboard" className="-mb-24 sm:-mb-32">
            <MiniDashboard />
          </BrowserFrame>
          <div className="pointer-events-none absolute -inset-x-10 top-10 -z-10 h-40 rounded-full bg-brand-civic/25 blur-3xl" />
        </motion.div>
      </div>

      <div className="h-28 bg-gradient-to-b from-transparent to-[#F5F7FA] sm:h-36" />
      <div className="h-16 bg-[#F5F7FA]" />
    </section>
  );
}
